import { pollUploadUntilDone, isUploadBatchComplete } from "./uploadPoll";
import { listenUploadProgress } from "./uploadSocket";
import { emitDashboardRefresh } from "./dashboardEvents";
import { repoCaseService } from "../services/repoCase.service";
import { parseUploadBatchId } from "./uploadBatchId";

/**
 * Wait for upload batch completion using Socket.IO (live) + polling fallback.
 */
export async function waitForUploadBatch(batchId, token, { onProgress } = {}) {
  const id = parseUploadBatchId(batchId);
  let resolved = false;
  let rejectFn = null;

  const pollPromise = pollUploadUntilDone(id, token, { onProgress }).then(
    (batch) => {
      resolved = true;
      return batch;
    }
  );

  const socketPromise = new Promise((resolve, reject) => {
    rejectFn = reject;
    const cleanup = listenUploadProgress(token, id, {
      onProgress: (payload) => {
        if (typeof onProgress === "function") {
          onProgress({
            processedRows: payload.processedRows,
            totalRows: payload.totalRows,
            status: payload.status,
            message: payload.message,
            importNote: payload.message,
          });
        }
      },
      onComplete: (payload) => {
        const batch = payload.batch || payload;
        if (!isUploadBatchComplete(batch)) return;
        cleanup();
        if (!resolved) resolve(batch);
      },
      onFailed: (payload) => {
        cleanup();
        if (!resolved) {
          reject(
            new Error(
              payload.errorMessage ||
                payload.message ||
                payload.batch?.errorMessage ||
                payload.batch?.message ||
                "Upload failed. Please check the Excel file and try again."
            )
          );
        }
      },
    });

    setTimeout(() => cleanup(), 4 * 60 * 60 * 1000);
  });

  try {
    const batch = await Promise.race([
      pollPromise,
      socketPromise.then(async (batch) => {
        if (batch && batch.status) return batch;
        return pollUploadUntilDone(id, token, { onProgress });
      }),
    ]);

    if (isUploadBatchComplete(batch)) {
      emitDashboardRefresh({ batchId: id, batch });
      if (token) {
        repoCaseService.warmSearchCache(token).catch(() => {});
      }
    }

    return batch;
  } catch (err) {
    if (rejectFn) rejectFn(err);
    throw err;
  }
}
